// Pure helper: turn a list of allocation items into a chart-ready
// slice array plus the totals the detail table needs.
//
// Slice shape: { groupId, name, raw, abs, percent }
//   - groupId:  stable per-row identity (`groupEntityId` preferred,
//               then the response-internal `groupId`, finally null).
//               Threaded so React keys and ECharts data-item `id`s
//               survive homonym rows (e.g. two "Cash" accounts at the
//               same grouping level). Display callers should fall
//               back to `name` when this is null.
//               NOTE: ECharts pie / treemap key internally by `name`
//               for legend + tooltip lookups, so two slices sharing
//               the exact same display name will still conflate in
//               those views regardless of `groupId`. A "disambiguate
//               display name" pass is a separate UX follow-up; this
//               threading is the React-side dedupe.
//   - name:     row.name as-is (the tooltip and table both render this)
//   - raw:      signed numeric value of the chosen measure on that row.
//               Always a non-zero finite number in `slices` -- non-numeric
//               values (null / undefined / NaN / +-Infinity) and zeros
//               are dropped from `slices` entirely via skippedZero. The
//               detail table renders the original row's value (including
//               dashes for nulls) through the unfiltered items array, so
//               those rows still appear in the table even when they're
//               not in the chart.
//   - abs:      Math.abs(raw); pies can't render negatives so slice
//               angles use this
//   - percent:  abs / totalAbs (0..1). Always non-negative; sums to 1
//               across non-skipped slices (or 0 when totalAbs is 0).
//
// Returns:
//   - slices:        sorted by abs desc (largest slice first)
//   - totalAbs:      sum of abs values
//   - totalRaw:      sum of signed raw values (this is what the detail
//                    table's footer shows; matches the household total
//                    for `scope: 'level'` allocations)
//   - hasNegatives:  true when any item has a negative raw value;
//                    drives the warning chip in the tool
//   - skippedZero:   count of items dropped from `slices` because their
//                    raw value was either non-numeric (null / undefined
//                    / NaN / Infinity) OR exactly 0. Either way the
//                    chart would have rendered an invisible wedge, so
//                    we drop them and let the unfiltered items array
//                    keep them visible in the detail table.

export function computeSlices (items, measureId) {
  if (!Array.isArray(items) || !measureId) {
    return { slices: [], totalAbs: 0, totalRaw: 0, hasNegatives: false, skippedZero: 0 }
  }

  let totalAbs = 0
  let totalRaw = 0
  let hasNegatives = false
  let skippedZero = 0
  const rich = []

  for (const item of items) {
    const raw = item?.[measureId]
    const numeric = typeof raw === 'number' && Number.isFinite(raw) ? raw : 0
    if (numeric === 0) {
      skippedZero++
      continue
    }
    if (numeric < 0) hasNegatives = true
    const abs = Math.abs(numeric)
    totalAbs += abs
    totalRaw += numeric
    // groupId: prefer groupEntityId (stable + numeric -- survives
    // renames), fall back to the response-internal groupId, then null.
    // The detail table and chart consumers compose this with `name`
    // for React keys; ECharts data items thread it as `id`.
    const groupId = item?.groupEntityId ?? item?.groupId ?? null
    rich.push({ groupId, name: item.name ?? 'Unknown', raw: numeric, abs })
  }

  // Second pass: assign percent now that we know totalAbs. Done after
  // accumulation so we don't need to walk twice. Percent of a slice
  // when totalAbs is 0 is 0 (every slice's raw must have been 0, but
  // those are already skipped above; this guard is defensive).
  const slices = rich
    .map(s => ({ ...s, percent: totalAbs > 0 ? s.abs / totalAbs : 0 }))
    .sort((a, b) => b.abs - a.abs)

  return { slices, totalAbs, totalRaw, hasNegatives, skippedZero }
}
